import { extend as _rawExtend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Drawer from 'flarum/common/utils/Drawer';

/**
 * 移动端抽屉「要点两下才关」(iOS Safari) —— 根因分析 & 本文件为何不再拦 touchmove
 *
 * 【症状】
 *   iPhone Safari 上: 移动端点页头 menu 按钮打开抽屉后, 点抽屉旁边的遮罩
 *   (.drawer-backdrop) 要点两下才关 —— 第一下完全没反应。Android Chrome 不复现。
 *
 * 【根因】(Apple WebKit "Handling Compound Events" 原文)
 *   "calling `preventDefault()` on a touchstart or the first touchmove event of a
 *    series prevents the corresponding mouse events from firing"
 *   ⇒ 只要在一次触摸序列的**第一次 touchmove** 上调了 preventDefault(),
 *     浏览器就**不再合成对应的 mouse 事件**, click 根本不产生。
 *
 *   而 Flarum 2.0 抽屉的关闭路径**只有 click**
 *   (vendor/flarum/core/js/src/common/utils/Drawer.js):
 *     - L27: document.getElementById('content').addEventListener('click', ...) → this.hide()
 *     - L114: this.$backdrop = $('<div/>').addClass('drawer-backdrop fade')
 *             .appendTo('body').on('click', this.hide.bind(this));
 *     - **没有任何 touchend / pointerup 关闭路径**
 *
 *   ⇒ iPhone 上手指落下那 1~2px 抖动就产生 touchmove → 被本文件这段代码 cancel
 *     → 浏览器不再合成 click → **抽屉唯一的关闭触发器被掐断**
 *     → 第一次点不动; 第二次用户点得准、没抖 → click 正常 → 才关。
 *   ⇒ 纯 iOS Safari 症状: Android Chrome 不复现 (Chrome 只在 cancel touchstart 时
 *     才抑制 click)。
 *
 * 【生产实测证据】(2026-10-21, bjxy.top, iPhone UA + 390×844 + hasTouch)
 *   | 实测项                                   | 结果                                                                        |
 *   |------------------------------------------|-----------------------------------------------------------------------------|
 *   | window.zivenCore.isMobile()             | true (⇒ 那个 handler 确实绑上了)                                           |
 *   | .drawer-backdrop 上的 jQuery 事件         | {click: 1, touchmove: 1}                                                    |
 *   | 在 backdrop 上派发一个**可取消**的 touchmove | defaultPrevented: true (⇒ preventDefault **真实生效**, 不是 passive 空转 ⇒ WebKit 规则适用) |
 *   | 手工给 backdrop 派发 click               | 抽屉**立刻关闭** (drawerOpen: true → false) ⇒ core 的关闭路径本身完全正常, 问题只在 iOS **不合成** click |
 *
 * 【为什么删掉是安全的】(关键: 那段 handler 现在**纯属多余**)
 *   抽屉打开时背景滚不动的锁, **不是**这两段 handler 提供的:
 *   1. **Flarum core 自己**: vendor/flarum/core/less/common/App.less:155-157
 *      的 `.drawerOpen { overflow: hidden; }` (锁 .App 容器)
 *   2. **ziven-theme-aura 提供 body 锁**: packages/ziven-theme-aura/less/forum.less:680-690
 *         @media (max-width: 991.98px) {
 *             body:has(.App.drawerOpen) {
 *                 overflow: hidden; position: fixed; width: 100%;
 *                 top: var(--ziven-aura-drawer-scroll-top, 0);
 *             }
 *         }
 *      (.App 就是 #app —— Drawer.js:110 往 #app 上加 drawerOpen class)
 *
 *   对照实验 (生产实测, 三组):
 *   | 状态                                      | scrollHeight | maxScroll | body 计算值                                | 背景能滚?                              |
 *   |-------------------------------------------|--------------|-----------|--------------------------------------------|---------------------------------------|
 *   | 抽屉**关**                                 | 5312         | 4468      | position: static / overflow: hidden scroll | **能** (scrollTo(0,800) ⇒ scrollY=800 ✓) |
 *   | 抽屉**开**                                 | 844          | 0         | position: fixed / overflow: hidden         | **不能** ✓                              |
 *   | 抽屉**开** + **把两段 touchmove handler 解绑** | 844          | 0         | position: fixed / overflow: hidden         | **仍然不能** ✓                          |
 *   ⇒ **解绑之后背景照样滚不动** (maxScroll 仍是 0), 而解绑之后 click 就能正常合成
 *     ⇒ **删掉是零代价、纯收益**。
 *
 *   附带修掉一个跨扩展地雷: 原先配套的 hide 块里是
 *   `$('.drawer-backdrop').off('touchmove')` —— **不带事件命名空间**,
 *   会把 .drawer-backdrop / .App-sidebar-user-block 上**别的扩展绑的 touchmove
 *   handler 一并干掉**。生产上 theme-aura 恰好没在上面绑 touchmove 才没炸。
 *
 * 【全局扫描】
 *   整个 packages/ 树里, touchmove + preventDefault() **只有这一处**。
 *   (ziven-guaguale 的 GuaGuaLeScratchModal.js 是画布拖拽、
 *    ziven-scheduler 的 CalendarTab.js 是手势方向判定 —— passive 且不 preventDefault,
 *    都不是「掐 click」的路子。)
 *
 * 【移除条件】
 *   如果将来 **Flarum core 自己**给抽屉补上了 touchend / pointerup 关闭路径
 *   (不再只靠 click), 本段注释可以补充说明; 本删除本身不需要回滚。
 *
 * 【本地验证边界】
 *   headless Chrome 测不出 iOS「不合成 click」那一步 (Chrome 本来就照常合成 click)
 *   ⇒ iOS 真机的双击现象本身必须在 iPhone 真机上复测, 不能只看本地测试结果。
 */
export default () => {
  // (辉哥 9/17 16:47 派 Coder 修): webpack DCE 把 const helper globals 去掉,
  // 改用 function declaration 让 helper 名字在函数体内可用
  function safeExtend(targetOrClass, method, fn) {
    if (!window.__zivenCoreSafeExtend) {
      console.warn('[ziven-core] safeExtend helper not loaded, skipped:', method);
      return null;
    }
    return window.__zivenCoreSafeExtend(targetOrClass, method, fn);
  }
  function safeOverride(targetOrClass, method, fn) {
    if (!window.__zivenCoreSafeOverride) {
      console.warn('[ziven-core] safeOverride helper not loaded, skipped:', method);
      return null;
    }
    return window.__zivenCoreSafeOverride(targetOrClass, method, fn);
  }
  safeExtend(Drawer, 'show', function () {
    const isMobile = window.zivenCore.isMobile();
    const currentUser = app.session.user;
    if (!isMobile) {
      return;
    }
    const sideBarContainer = $(".App-sidebar-items-container");
    if (sideBarContainer.hasClass("ModifiedSideBar") === false) {
      if (!currentUser) {
        $(".App-sidebar-items-container").addClass("ModifiedSideBar");
        $(".App-sidebar-items-container").prepend("<div class='DrawerHeadIcon'></div>");
      }
    }
    $('.App-sidebar-container').animate({
      scrollTop: 0
    }, 0);
  });
};